import { useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "./apiClient";
import moment from "moment";
import apiFastifyClient from "./apiFastifyCloud";
import {
  IGolfCartRequestsResponse,
  IGolfRequestsParams,
} from "src/types/golf-carts";

interface IParams {
  type: string; //"active" | "upcoming"
  status?: string; //"open" | "closed" ...etc
  clientId?: string;
  cityId?: string;
  location?: string;
  paymentTypes?: string[];
  serviceTypes?: string[];
  service?: string; //"valet" | "selfParking"
  from?: string;
  to?: string;
  search?: string;
  customerNumber?: string;
  limit?: string;
  startAfter?: string | undefined;
}

export const useGetTransactionsList = (params: IParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: [
      "get-transactions-list",
      params.type,
      params?.from,
      params?.to,
      params?.location,
      params?.service,
      params?.limit,
      ...(params?.paymentTypes || []),
      params.search,
      params.customerNumber,
      params.startAfter,
      params.clientId,
      params.cityId,
      params.status,
    ],
    queryFn: async () => {
      return apiClient.get(`/transaction`, { params });
    },
    enabled:
      !!params.customerNumber ||
      (!!params?.from &&
        !!params.to &&
        (!!params.location || !!params.cityId) &&
        ((moment(+params.to).diff(moment(+params.from), "days") < 31 && // to not exceed 31 days when active
          params.type === "active") ||
          params.type === "upcoming")),
  });

export const useExportTransactionsList = (params: IParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: ["export-transactions-list"],
    queryFn: async () => {
      return apiClient.get(`/transaction/export`, { params });
    },
    gcTime: 0,
    enabled: false,
  });

interface IValidationsParams {
  search?: string;
  location?: string;
  clientId?: string;
  cityId?: string;
  from?: string;
  to?: string;
  approvedBy?: string;
  limit?: number;
  page?: number;
}

export const useGetRecentTicketsValidationsList = (
  params?: IValidationsParams,
) =>
  useQuery<
    void,
    APIErrorResponse,
    {
      data: {
        id: string;
      }[];
      total: number;
    }
  >({
    queryKey: [
      "get-employees-tickets-validations-list",
      params?.search,
      params?.location,
      params?.clientId,
      params?.limit,
      params?.page,
      params?.cityId,
      params?.approvedBy,
      params?.from,
      params?.to,
    ],
    queryFn: async () => {
      return apiClient.get(`/ticket-validation/admin/recent`, {
        params: params ?? {},
      });
    },
    gcTime: 1 * 60 * 60 * 1000, // 1 Hour
    enabled: !!params?.location || !!params?.clientId || !!params?.search,
  });

export const useExportRecentTicketsValidations = (params: IValidationsParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: ["export-tickets-validations-list"],
    queryFn: async () => {
      return apiClient.get(`/ticket-validation/admin/recent/export`, {
        params,
      });
    },
    gcTime: 0,
    enabled: false,
  });

export const useGetTicketsValidationApprovalEmployees = (params: {
  locationId?: string;
  clientId?: string;
}) =>
  useQuery<
    void,
    APIErrorResponse,
    {
      data: {
        id: string;
        name: string;
      }[];
    }
  >({
    queryKey: [
      "get-tickets-validations-approval-employees",
      params?.locationId,
      params?.clientId,
    ],
    queryFn: async () => {
      return apiClient.get(`/ticket-validation/users`, {
        params: params ?? {},
      });
    },
    gcTime: 0,
    enabled: !!params?.locationId || !!params?.clientId,
  });

export const useGetGolfCartRequests = (params?: IGolfRequestsParams) =>
  useQuery<void, APIErrorResponse, IGolfCartRequestsResponse>({
    queryKey: ["get-golf-cart-requests", params],
    queryFn: async () => {
      return apiFastifyClient.get(`/v1/requests`, { params });
    },
    gcTime: 0,
  });

export const useExportGolfCartRequests = (params: IGolfRequestsParams) =>
  useQuery<void, APIErrorResponse, any>({
    queryKey: ["export-golf-cart-requests"],
    queryFn: async () => {
      return apiFastifyClient.get(`/v1/requests/export`, { params });
    },
    gcTime: 0,
    enabled: false,
  });

export const useGetGolfCartRequestById = ({ id }: { id: string }) =>
  useQuery<any, APIErrorResponse, { data: any }>({
    queryKey: ["get-driver-by-id"],
    queryFn: async () => {
      return apiFastifyClient.get(`/v1/requests/${id}`);
    },
    gcTime: 0,
    enabled: !!id,
  });
